import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Vehicle } from '@otw/models/inventory'
import React, { useEffect, useMemo, useState } from 'react'
import { usePermissions } from 'hooks/auth/usePermissions'
import { useVehicleGrayBookPrices } from 'hooks/inventory/useVehicleGrayBookPrices'
import {
  calculateVehicleLtv,
  calculateVehicleProfit,
  parseListPrice,
} from './util/calculateVehiclePriceMetrics'

interface BulkPriceUpdateDialogProps {
  open: boolean
  onClose: () => void
  vehicles: Vehicle[]
  onSubmit: (updates: Array<{ id: string; optic_list_price: number }>) => void
  loading?: boolean
}

const formatCurrency = (value?: number | null): string =>
  value == null || Number.isNaN(Number(value))
    ? '—'
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
      }).format(Number(value))

const formatPercent = (value?: number | null): string =>
  value == null || Number.isNaN(Number(value)) ? '—' : `${value.toFixed(1)}%`

const getLtvColor = (ltv: number | null): string => {
  if (ltv == null) return 'text.secondary'
  if (ltv > 105) return 'success.main'
  if (ltv < 95) return 'error.main'
  return 'primary.main'
}

const getProfitColor = (profit: number | null): string => {
  if (profit == null) return 'text.secondary'
  if (profit > 0) return 'success.main'
  if (profit < 0) return 'error.main'
  return 'text.primary'
}

const MetricItem = ({
  label,
  value,
  valueColor,
}: {
  label: string
  value: string
  valueColor?: string
}) => (
  <Box>
    <Typography variant='caption' color='text.secondary' display='block'>
      {label}
    </Typography>
    <Typography variant='body2' fontWeight={600} color={valueColor || 'text.primary'}>
      {value}
    </Typography>
  </Box>
)

const BulkPriceUpdateDialog: React.FC<BulkPriceUpdateDialogProps> = ({
  open,
  onClose,
  vehicles,
  onSubmit,
  loading = false,
}) => {
  const [pricesByVehicleId, setPricesByVehicleId] = useState<Record<string, string>>({})
  const [applyAllPrice, setApplyAllPrice] = useState('')
  const { checkPermission } = usePermissions()
  const canViewProfit = checkPermission('inventory', 'view_profit')
  const { grayBookPricesByVehicleId, isLoadingGrayBookPrices } = useVehicleGrayBookPrices(vehicles, open)

  useEffect(() => {
    if (!open) return

    const initial = vehicles.reduce<Record<string, string>>((acc, vehicle) => {
      acc[vehicle.id] =
        vehicle.optic_list_price != null && !Number.isNaN(Number(vehicle.optic_list_price))
          ? String(vehicle.optic_list_price)
          : ''
      return acc
    }, {})

    setPricesByVehicleId(initial)
    setApplyAllPrice('')
  }, [open, vehicles])

  const parsedUpdates = useMemo(() => {
    return vehicles
      .map(vehicle => {
        const nextPrice = parseListPrice(pricesByVehicleId[vehicle.id])
        if (nextPrice == null) return null

        const currentPrice = vehicle.optic_list_price == null ? null : Number(vehicle.optic_list_price)
        if (currentPrice === nextPrice) return null

        return { id: vehicle.id, optic_list_price: nextPrice }
      })
      .filter((update): update is { id: string; optic_list_price: number } => update !== null)
  }, [vehicles, pricesByVehicleId])

  const hasInvalidPrice = useMemo(() => {
    return vehicles.some(vehicle => parseListPrice(pricesByVehicleId[vehicle.id]) == null)
  }, [vehicles, pricesByVehicleId])

  const handlePriceChange = (vehicleId: string, value: string) => {
    setPricesByVehicleId(prev => ({ ...prev, [vehicleId]: value }))
  }

  const handleApplyAll = () => {
    if (applyAllPrice.trim() === '') return
    const nextPrice = Number(applyAllPrice)
    if (Number.isNaN(nextPrice) || nextPrice < 0) return

    setPricesByVehicleId(prev => {
      const next = { ...prev }
      vehicles.forEach(vehicle => {
        next[vehicle.id] = applyAllPrice
      })
      return next
    })
  }

  const handleSubmit = () => {
    if (parsedUpdates.length === 0 || hasInvalidPrice) return
    onSubmit(parsedUpdates)
  }

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth='md' fullWidth keepMounted={false}>
      <DialogTitle>Update Optic List Price</DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Enter a new Optic List Price for each selected vehicle. LTV
          {canViewProfit ? ' and profit' : ''} update as you edit. Only changed prices will be saved.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
          <TextField
            label='Set all to'
            type='number'
            size='small'
            value={applyAllPrice}
            onChange={e => setApplyAllPrice(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
            InputProps={{
              startAdornment: <InputAdornment position='start'>$</InputAdornment>,
            }}
            sx={{ flex: 1 }}
            disabled={loading}
          />
          <Button variant='outlined' onClick={handleApplyAll} disabled={loading || applyAllPrice.trim() === ''}>
            Apply to all
          </Button>
        </Stack>

        <Box sx={{ maxHeight: 480, overflow: 'auto' }}>
          <Stack spacing={1.5}>
            {vehicles.map(vehicle => {
              const listPrice = parseListPrice(pricesByVehicleId[vehicle.id])
              const grayBookRetailPrice = grayBookPricesByVehicleId[vehicle.id] ?? null
              const ltv = calculateVehicleLtv(listPrice, grayBookRetailPrice)
              const profit = canViewProfit ? calculateVehicleProfit(vehicle, listPrice) : null
              const metricGridSize = canViewProfit ? 3 : 4

              return (
                <Paper key={vehicle.id} variant='outlined' sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant='body1' fontWeight='medium'>
                        {vehicle.stock_number || 'No stock #'} — {vehicle.year} {vehicle.make} {vehicle.model}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        Current Optic List Price: {formatCurrency(vehicle.optic_list_price)}
                      </Typography>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={4}>
                        <MetricItem
                          label='Seller Asking Price'
                          value={formatCurrency(vehicle.seller_asking_price)}
                        />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <MetricItem label='Bought From' value={vehicle.bought_from?.trim() || '—'} />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <MetricItem label='Who' value={vehicle.who?.trim() || '—'} />
                      </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={metricGridSize}>
                        <MetricItem
                          label='Gray Book Retail'
                          value={
                            isLoadingGrayBookPrices && grayBookRetailPrice == null ? 'Loading...' : formatCurrency(grayBookRetailPrice)
                          }
                        />
                      </Grid>
                      <Grid item xs={6} sm={metricGridSize}>
                        <MetricItem label='LTV' value={formatPercent(ltv)} valueColor={getLtvColor(ltv)} />
                      </Grid>
                      <Grid item xs={6} sm={metricGridSize}>
                        <MetricItem
                          label='Est. Repair Cost'
                          value={formatCurrency(vehicle.estimated_repair_cost)}
                        />
                      </Grid>
                      {canViewProfit && (
                        <Grid item xs={6} sm={metricGridSize}>
                          <MetricItem
                            label='Profit'
                            value={formatCurrency(profit)}
                            valueColor={getProfitColor(profit)}
                          />
                        </Grid>
                      )}
                    </Grid>

                    <TextField
                      label='New Optic List Price'
                      type='number'
                      size='small'
                      value={pricesByVehicleId[vehicle.id] ?? ''}
                      onChange={e => handlePriceChange(vehicle.id, e.target.value)}
                      inputProps={{ min: 0, step: '0.01' }}
                      InputProps={{
                        startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                      }}
                      fullWidth
                      disabled={loading}
                      error={parseListPrice(pricesByVehicleId[vehicle.id]) == null}
                    />
                  </Stack>
                </Paper>
              )
            })}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={loading || hasInvalidPrice || parsedUpdates.length === 0}
          startIcon={loading ? <CircularProgress size={16} color='inherit' /> : undefined}
        >
          {loading
            ? 'Updating...'
            : `Update ${parsedUpdates.length || ''} Price${parsedUpdates.length === 1 ? '' : 's'}`.trim()}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default BulkPriceUpdateDialog
